import React, { useEffect } from 'react';
import { FormType } from '../../../constants/constants';
import LanguageUtils from '../../../utils/LanguageUtils';
import { useDispatch, useSelector } from 'react-redux';
import CustomerCancellationRuleSet from '../../../interfaces/output/customerCancellationRuleSet';
import { createCustomerCancellationRuleSet, fetchCustomerCancellationRuleSetById, getCustomerCancellationRuleSet, isCreatingCustomerCancellationRuleSet,
    isLoadingCustomerCancellationRuleSetById, isUpdatingCustomerCancellationRuleSet, updateCustomerCancellationRuleSet } from '../../../reducers/cancellationRuleSetReducer';
import WorkflowUtils from '../../../utils/workflowUtils';
import GenericStep from '../../../interfaces/common/genericStep';
import StepperAdditionalInformation from '../../common/widgets/stepperAdditionalInformation';
import GenericStepper from '../../common/widgets/genericStepper';
import UrlConstants from '../../../constants/UrlConstants';
import AddCustomerCancellationRuleSetStep1 from './addCustomerCancellationRuleSetStep1';
import AddCustomerCancellationRuleSetStep2 from './addCustomerCancellationRuleSetStep2';
import AddCustomerCancellationRuleSetStep3 from './addCustomerCancellationRuleSetStep3';
import CancellationRuleSetSelectionValidation from './cancellationRuleSetSelectionValidation';
import CustomerCancellationRuleSetPriorityAndValidityValidation from './customerCancellationRuleSetPriorityAndValidityValidation';
import CustomerSelectionValidation from './customerSelectionValidation';

interface IAddCustomerCancellationRuleSetProps {
    customerId?: number;
    cancellationRuleSetId?: number;
    customerCancellationRuleSetId?: number;
    steps: Array<number>;
    type: FormType;
}

const messages = {
    edit: LanguageUtils.createMessage('Edit customer cancellation rule set'),
    add: LanguageUtils.createMessage('Add customer cancellation rule set')
};

export default function AddCustomerCancellationRuleSetForm(props: IAddCustomerCancellationRuleSetProps): JSX.Element {
    const dispatch = useDispatch();
    const { customerId, cancellationRuleSetId, customerCancellationRuleSetId, steps, type } = props;
    const [customerCancellationRuleSet, setCustomerCancellationRuleSet] = React.useState<CustomerCancellationRuleSet>({} as CustomerCancellationRuleSet);
    const [selectedId, setSelectedId] = React.useState<number>();
    const propsCustomerCancellationRuleSet = useSelector(getCustomerCancellationRuleSet);
    const [redirectCondition, setRedirectCondition] = React.useState<boolean>(false);
    const propsIsCreatingCustomerCancellationRuleSet = useSelector(isCreatingCustomerCancellationRuleSet);
    const prevIsCreatingCustomerCancellationRuleSet = WorkflowUtils.usePrevious<boolean>(propsIsCreatingCustomerCancellationRuleSet);
    const propsIsUpdatingCustomerCancellationRuleSet = useSelector(isUpdatingCustomerCancellationRuleSet);
    const prevIsUpdatingCustomerCancellationRuleSet = WorkflowUtils.usePrevious<boolean>(propsIsUpdatingCustomerCancellationRuleSet);
    const propsIsLoadingCustomerCancellationRuleSetById = useSelector(isLoadingCustomerCancellationRuleSetById);
    const prevIsLoadingCustomerCancellationRuleSetById = WorkflowUtils.usePrevious<boolean>(propsIsLoadingCustomerCancellationRuleSetById);

    useEffect(() => {
        const lastStep = steps[steps.length - 1];

        switch (lastStep) {
            case 3:
                if(prevIsUpdatingCustomerCancellationRuleSet === true && !propsIsUpdatingCustomerCancellationRuleSet ||
                    prevIsCreatingCustomerCancellationRuleSet === true && !propsIsCreatingCustomerCancellationRuleSet) {
                    setRedirectCondition(true);
                }
                break;

            default:
        }

        return () => setRedirectCondition(false);
    }, [propsIsUpdatingCustomerCancellationRuleSet, propsIsCreatingCustomerCancellationRuleSet]);

    useEffect(() => {
        if(customerCancellationRuleSetId) {
            dispatch(fetchCustomerCancellationRuleSetById(customerCancellationRuleSetId));
        }
    }, [dispatch]);

    // useEffect(() => {
    //     if(!propsIsLoadingCustomerCancellationRuleSetById && prevIsLoadingCustomerCancellationRuleSetById) {
    //         setCustomerCancellationRuleSet(propsCustomerCancellationRuleSet);
    //     }

    // }, [propsIsUpdatingCustomerCancellationRuleSet, propsCustomerCancellationRuleSet.id]);

    useEffect(() => {
        if(!propsIsLoadingCustomerCancellationRuleSetById && prevIsLoadingCustomerCancellationRuleSetById) {
            setCustomerCancellationRuleSet(propsCustomerCancellationRuleSet);
        }
    }, [propsIsLoadingCustomerCancellationRuleSetById]);

    const onChange = (attribute: string, value: any) => {
        const newCustomerCancellationRuleSet = { ...customerCancellationRuleSet } as CustomerCancellationRuleSet;
        (newCustomerCancellationRuleSet as any)[attribute] = value;

        setCustomerCancellationRuleSet(newCustomerCancellationRuleSet);
    };

    const onChangeList = (value: number) => {
        setSelectedId(value);
    };

    const getSteps = (): GenericStep[] => [
        {
            id: 1,
            name: 'Select cancellation rule set from list',
            xl: true,
            validationFn: () => CancellationRuleSetSelectionValidation.validateCustomerCancellationRuleSetForm(selectedId),
            content: <AddCustomerCancellationRuleSetStep1 onChange={onChangeList} customerCancellationRuleSet={selectedId} />
        } as GenericStep,
        {
            id: 2,
            name: 'Select customer from list',
            xl: true,
            validationFn: () => CustomerSelectionValidation.validateCustomerCancellationRuleSetForm(selectedId),
            content: <AddCustomerCancellationRuleSetStep2 onChange={onChangeList} customerCancellationRuleSet={selectedId} />
        } as GenericStep,
        {
            id: 3,
            name: 'Enter validity and priority',
            content: <AddCustomerCancellationRuleSetStep3 onChange={onChange} customerCancellationRuleSet={customerCancellationRuleSet} />,
            validationFn: () => CustomerCancellationRuleSetPriorityAndValidityValidation.validateCustomerCancellationRuleSetForm(customerCancellationRuleSet),
            onNext: () => {
                if(!customerCancellationRuleSet.id) {
                    const newCustomerCancellationRuleSet = { ...customerCancellationRuleSet } as CustomerCancellationRuleSet;
                    if(type === FormType.AddCustomerCancellationRuleSetFromCancellationRuleSet) {
                        newCustomerCancellationRuleSet.customerId = selectedId;
                        newCustomerCancellationRuleSet.cancellationRuleSetId = cancellationRuleSetId;
                    }
                    else {
                        newCustomerCancellationRuleSet.customerId = customerId;
                        newCustomerCancellationRuleSet.cancellationRuleSetId = selectedId;
                    }
                    dispatch(createCustomerCancellationRuleSet(newCustomerCancellationRuleSet));
                }
                else {
                    dispatch(updateCustomerCancellationRuleSet(customerCancellationRuleSet));
                }
            }
        } as GenericStep
    ];

    const getTitle = (): any => {
        switch (type) {
            case FormType.AddCustomerCancellationRuleSetFromCancellationRuleSet:
            case FormType.AddCustomerCancellationRuleSetFromCustomer:
                return messages.add;
            case FormType.Edit:
                return messages.edit;
            default:
                throw new Error('Form type required');
        }
    };

    const includedSteps = getSteps().filter(step => steps.includes(step.id));

    return (
        <GenericStepper
            steps={includedSteps}
            name={getTitle()}
            redirectCondition={redirectCondition}
            redirectTo={customerId ? `/${UrlConstants.CUSTOMERS}/${customerId}/customerCancellationRuleSets` :
                customerCancellationRuleSet.cancellationRuleSet?.id ? `/${UrlConstants.CANCELLATION_RULE_SET}/${customerCancellationRuleSet.cancellationRuleSet?.id}/customerCancellationRuleSets` :
                    `/${UrlConstants.CANCELLATION_RULE_SET}/${cancellationRuleSetId}/customerCancellationRuleSets`}
            additionalDetails={<StepperAdditionalInformation customerId={customerId} />}
        />
    );
}